import { useEffect, useMemo, useState } from 'react';
import '../styles/dashboard-progress-management.css';
import EditableRecordTable from '../components/EditableRecordTable.jsx';
import DeliverableSetEditor from '../components/DeliverableSetEditor.jsx';
import DashboardInputLayout from '../components/DashboardInputLayout.jsx';
import DashboardInputToolbar from '../components/DashboardInputToolbar.jsx';
import DashboardFilterPanel from '../components/DashboardFilterPanel.jsx';
import JoinedSummaryCell from '../components/JoinedSummaryCell.jsx';
import {
  createProgressSchedule,
  deleteProgressSchedule,
  getInitData,
  getProgressSchedules,
  getScheduleOemRegionPairs,
  getDeliverableManagements,
  updateProgressSchedule,
} from '../api/dashboardApi.js';
import { exportToExcel } from '../utils/exportUtils.js';
import { buildConflictMessage } from '../utils/conflictMessage.js';
import ExportConfirmPopup from '../components/ExportConfirmPopup.jsx';
import { getUserName } from '../../../auth/auth.js';
import { canEdit } from '../../../auth/permissions.js';
import {
  buildSelectOptions,
  buildMiddleCategoryOptionsByLarge,
  buildSmallCategoryOptionsByMiddle,
  buildSmallCategoryLookup,
  normalizeRows,
  buildSuccessorTaskOptionsByOemRegion,
  buildMasterRecordOptions,
  buildOptionsByOemRegion,
  buildLookupMap,
  buildMiddleCategoryLookup,
  toPayload,
  validateDeliverables,
  buildDeliverableOptionsByCoordinate,
  getDeliverableOptionsForSchedule,
} from '../utils/converters.js';

const BASE_COLUMNS = [
  { key: 'deadline_check', label: '期限チェック', type: 'text', readOnly: true },
  { key: 'oem_region', label: 'OEM×対地', type: 'select', width: '180px', required: true },
  { key: 'category_name', label: 'カテゴリ', type: 'select', required: true },
  { key: 'large_category_name', label: '大項目', type: 'select', required: true },
  { key: 'middle_category_name', label: '中項目', type: 'select', required: true },
  { key: 'small_category', label: '小項目', type: 'select', width: '300px', required: true },
  { key: 'phase_name', label: 'Phase', type: 'select' },
  { key: 'milestone', label: 'マイルストーン', type: 'checkbox' },
  // 成果物は詳細レコードの成果物セット（3行1まとまり）で編集する。
  // グリッド列は集約値の読み取り専用サマリ（Excel出力にも使用）。
  // 編集用の deliverables 列は columns useMemo 側で付与する（BASE_COLUMNS を変えない）。
  // 複数の成果物が紐付く行は名前もURLもカンマ結合で返るため、一覧では先頭＋件数に畳む。
  // 件数は結合文字列ではなく deliverables 配列から数える。成果物名は自由入力で
  // 「要件説明資料, WBS(RFXがある場合のみ)」のように区切り文字を含むことがある。
  // URL は結合文字列をそのまま href にするとリンクが壊れるので、この表示が必須。
  // 全件は詳細ポップアップの成果物セットとエクスポートで確認できる。
  {
    key: 'deliverable_name', label: '成果物名', type: 'text', readOnly: true, popupHidden: true,
    renderDisplay: (value, row) => (
      <JoinedSummaryCell items={(row.deliverables || []).map((d) => d.product)} />
    ),
  },
  { key: 'is_deliverable_stored', label: '成果物保管済み（領域担当者更新）', type: 'checkbox', readOnly: true, popupHidden: true },
  { key: 'pmo_deliverable_check', label: 'PMO成果物保管チェック', type: 'checkbox', readOnly: true, popupHidden: true },
  {
    key: 'deliverable_storage_url', label: '成果物保管URL', type: 'url', readOnly: true, popupHidden: true,
    renderDisplay: (value, row) => (
      <JoinedSummaryCell items={(row.deliverables || []).map((d) => d.deliverable_url)} asLink />
    ),
  },
  { key: 'actor_name', label: '領域', type: 'select', required: true },
  { key: 'assignees_display', label: '担当者', type: 'tags' },
  { key: 'status_name', label: 'ステータス', type: 'select' },
  { key: 'start_date_plan', label: '開始日（予定）', type: 'date' },
  { key: 'end_date_plan', label: '終了日（予定）', type: 'date' },
  { key: 'start_date_achievement', label: '開始日（実績）', type: 'date' },
  { key: 'end_date_achievement', label: '終了日（実績）', type: 'date' },
  { key: 'successor_task', label: '後続タスク', type: 'tags' },
  { key: 'related_risk', label: '関連リスク', type: 'tags' },
  { key: 'related_problem', label: '関連課題', type: 'tags' },
  { key: 'memo', label: '備考', type: 'text' },
  { key: 'created_at', label: '作成日時', type: 'text', readOnly: true },
  { key: 'created_by', label: '作成者', type: 'text', readOnly: true },
  { key: 'updated_at', label: '最終更新日時', type: 'text', readOnly: true },
  { key: 'updated_by', label: '最終更新者', type: 'text', readOnly: true },
  { key: 'inconsistency_check', label: '不整合チェック', type: 'text', readOnly: true },
];


function createEmptyRow(/* istanbul ignore next -- 呼び出し元は常に defaults を渡す */ defaults = {}) {
  return BASE_COLUMNS.reduce((acc, column) => {
    if (!(column.key in acc)) {
      acc[column.key] = column.type === 'checkbox' ? false : '';
    }
    return acc;
  }, { ...defaults, __isNew: true });
}

export default function DashboardProgressManagementPage() {
  const [rows, setRows] = useState([]);
  const [riskOptions, setRiskOptions] = useState(['']);
  const [problemOptions, setProblemOptions] = useState(['']);
  const [riskByTitle, setRiskByTitle] = useState(new Map());
  const [problemByTitle, setProblemByTitle] = useState(new Map());
  const [riskItems, setRiskItems] = useState([]);
  const [problemItems, setProblemItems] = useState([]);
  const [oemByName, setOemByName] = useState(new Map());
  const [regionByName, setRegionByName] = useState(new Map());
  const [categoryByName, setCategoryByName] = useState(new Map());
  const [actorByName, setActorByName] = useState(new Map());
  const [statusByName, setStatusByName] = useState(new Map());
  const [middleByPair, setMiddleByPair] = useState(new Map());
  const [middleByName, setMiddleByName] = useState(new Map());
  const [smallByPair, setSmallByPair] = useState(new Map());
  const [smallByName, setSmallByName] = useState(new Map());
  const [smallCategoryOptionsByMiddle, setSmallCategoryOptionsByMiddle] = useState({});
  const [phaseByName, setPhaseByName] = useState(new Map());
  const [phaseOptions, setPhaseOptions] = useState(['']);
  // 成果物候補: `${oem_region}||${small_category}` → [{ id, product, deliverable_url }]
  const [deliverableOptionsByKey, setDeliverableOptionsByKey] = useState(
    { byCoordinate: {}, unlinked: [] },
  );
  const [employeeByName, setEmployeeByName] = useState(new Map());
  const [employeeOptions, setEmployeeOptions] = useState(['']);
  const [oemRegionOptions, setOemRegionOptions] = useState(['']);
  const [oemRegionLookup, setOemRegionLookup] = useState(new Map());
  const [oemRegionFilter, setOemRegionFilter] = useState('');
  const [defaultCreateValues, setDefaultCreateValues] = useState({});
  const [deadlineCheckFilter, setDeadlineCheckFilter] = useState('');
  const [inconsistencyCheckFilter, setInconsistencyCheckFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [largeCategoryFilter, setLargeCategoryFilter] = useState('');
  const [middleCategoryFilter, setMiddleCategoryFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFromFieldFilter, setDateFromFieldFilter] = useState('start_date_plan');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFieldFilter, setDateToFieldFilter] = useState('end_date_plan');
  const [dateToFilter, setDateToFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState(getUserName());
  const [keywordFilter, setKeywordFilter] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [addSignal, setAddSignal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportPopupOpen, setExportPopupOpen] = useState(false);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError('');
        const [scheduleData, initData, oemRegionPairs, deliverableData] = await Promise.all([
          getProgressSchedules(),
          getInitData(),
          getScheduleOemRegionPairs(),
          getDeliverableManagements(),
        ]);
        if (!active) return;

        // 成果物候補は座標（OEM×対地＋カテゴリ〜Phase）単位。
        // 別の小項目で登録済みの成果物は選べないようにする。
        setDeliverableOptionsByKey(
          buildDeliverableOptionsByCoordinate(deliverableData, scheduleData),
        );

        const {
          oems: oemData,
          regions: regionData,
          categories: categoryData,
          middle_categories: middleCategoryData,
          small_categories: smallCategoryData,
          actors: actorData,
          statuses: statusData,
          phases: phaseData,
          employees: employeeData,
          // oem_regions is unused – OEM×対地 options come from getScheduleOemRegionPairs()
          risks: riskData,
          problems: problemData,
        } = initData;

        setRows(scheduleData);
        setRiskOptions(
          buildMasterRecordOptions(riskData, [
            (item) => item.title,
            (item) => item.noId,
            (item) => item.name,
            (item) => item.id,
          ]),
        );
        setProblemOptions(
          buildMasterRecordOptions(problemData, [
            (item) => item.summary,
            (item) => item.title,
            (item) => item.noId,
            (item) => item.name,
            (item) => item.id,
          ]),
        );
        // title/product → ID ルックアップ構築
        const riskLookup = new Map();
        (riskData || []).forEach((r) => { if (r.title && !riskLookup.has(r.title)) riskLookup.set(r.title, r.id); });
        setRiskByTitle(riskLookup);
        const problemLookup = new Map();
        (problemData || []).forEach((p) => {
          // 課題APIは title でなく summary（source=title）を返すため summary を優先
          const key = p.summary || p.title;
          if (key && !problemLookup.has(key)) problemLookup.set(key, p.id);
        });
        setProblemByTitle(problemLookup);
        setRiskItems(riskData || []);
        setProblemItems(problemData || []);
        setOemByName(buildLookupMap(oemData, 'oem'));
        setRegionByName(buildLookupMap(regionData, 'region'));
        setCategoryByName(buildLookupMap(categoryData, 'category'));
        setActorByName(buildLookupMap(actorData, 'actor'));
        setStatusByName(buildLookupMap(statusData, 'status'));
        const middleLookup = buildMiddleCategoryLookup(middleCategoryData);
        setMiddleByPair(middleLookup.pairMap);
        setMiddleByName(middleLookup.byNameMap);
        const smallLookup = buildSmallCategoryLookup(smallCategoryData);
        setSmallByPair(smallLookup.pairMap);
        setSmallByName(smallLookup.byNameMap);
        setSmallCategoryOptionsByMiddle(buildSmallCategoryOptionsByMiddle(smallCategoryData));
        setPhaseByName(buildLookupMap(phaseData || [], 'phase'));
        setPhaseOptions(['', ...(phaseData || []).map((item) => item.phase).filter(Boolean)]);
        setEmployeeByName(buildLookupMap(employeeData, 'user_name', 'employee_id'));
        setEmployeeOptions(['', ...employeeData.map((item) => item.user_name).filter(Boolean)]);
        const oemRegionOpts = [''];
        const oemRegionMap = new Map();
        oemRegionPairs.forEach((p) => {
          if (!oemRegionMap.has(p.label)) {
            oemRegionOpts.push(p.label);
            oemRegionMap.set(p.label, { oemId: p.oem_id, regionId: p.region_id });
          }
        });
        setOemRegionOptions(oemRegionOpts);
        setOemRegionLookup(oemRegionMap);
        setDefaultCreateValues({
          status_name: '未着手',
        });
      } catch (err) {
        if (!active) return;
        setError('進捗管理データの取得に失敗しました');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  const groupedRows = useMemo(() => {
    const source = [...rows];
    source.sort((a, b) => {
      const keys = ['category_name', 'large_category_name', 'middle_category_name', 'small_category'];
      for (const key of keys) {
        const av = (a[key] || '').toString();
        const bv = (b[key] || '').toString();
        const cmp = av.localeCompare(bv, 'ja');
        if (cmp !== 0) return cmp;
      }
      /* istanbul ignore next -- 一覧の行は保存済みレコードなので必ず id を持つ */
      return (a.id || 0) - (b.id || 0);
    });

    return source;
  }, [rows]);

  const normalizedRows = useMemo(() => normalizeRows(groupedRows), [groupedRows]);

  const selectColumnKeys = useMemo(
    () => {
      const keys = BASE_COLUMNS.filter((column) => column.type === 'select').map((column) => column.key);
      const filterKeys = ['category_name', 'large_category_name', 'middle_category_name', 'actor_name', 'status_name'];
      filterKeys.forEach((k) => {
        /* istanbul ignore if -- フィルター対象は全て BASE_COLUMNS の select 列にある */
        if (!keys.includes(k)) keys.push(k);
      });
      return keys;
    },
    [],
  );

  const selectOptions = useMemo(
    () => buildSelectOptions(normalizedRows, selectColumnKeys),
    [normalizedRows, selectColumnKeys],
  );

  const middleCategoryOptionsByLarge = useMemo(
    () => buildMiddleCategoryOptionsByLarge(normalizedRows),
    [normalizedRows],
  );

  const successorTaskOptionsByOemRegion = useMemo(
    () => buildSuccessorTaskOptionsByOemRegion(normalizedRows),
    [normalizedRows],
  );

  // 関連リスク・関連課題の候補は行のOEM×対地に限定する（別OEM×対地への誤リンク防止）
  const relatedRiskOptionsByOemRegion = useMemo(() => {
    const grouped = buildOptionsByOemRegion(riskItems, [(item) => item.title]);
    // 同一OEM×対地の既存行に入力済みの値も候補へ含める
    normalizedRows.forEach((row) => {
      const key = row.oem_region || '';
      if (!key || !row.related_risk) return;
      if (!grouped[key]) grouped[key] = [''];
      String(row.related_risk).split(',').map((s) => s.trim()).filter(Boolean)
        .forEach((s) => { if (!grouped[key].includes(s)) grouped[key].push(s); });
    });
    return grouped;
  }, [normalizedRows, riskItems]);

  const relatedProblemOptionsByOemRegion = useMemo(() => {
    // 課題APIは title でなく summary（source=title）を返すため summary を優先
    const grouped = buildOptionsByOemRegion(problemItems, [(item) => item.summary, (item) => item.title]);
    normalizedRows.forEach((row) => {
      const key = row.oem_region || '';
      if (!key || !row.related_problem) return;
      if (!grouped[key]) grouped[key] = [''];
      String(row.related_problem).split(',').map((s) => s.trim()).filter(Boolean)
        .forEach((s) => { if (!grouped[key].includes(s)) grouped[key].push(s); });
    });
    return grouped;
  }, [normalizedRows, problemItems]);

  const deliverablesColumn = useMemo(
    () => ({
      key: 'deliverables',
      label: '成果物',
      type: 'custom',
      hidden: true, // グリッドには出さず、詳細レコード（ポップアップ）でのみ編集
      renderEditor: ({ row, value, update }) => (
        <DeliverableSetEditor
          value={value}
          onChange={update}
          options={getDeliverableOptionsForSchedule(deliverableOptionsByKey, row)}
        />
      ),
      renderDisplay: (value, row, /* istanbul ignore next -- 常に opts 付きで呼ばれる */ opts = {}) => {
        /* istanbul ignore next -- 成果物は常に配列として保持される */
        const list = Array.isArray(value) ? value : [];
        /* istanbul ignore else -- この列はグリッド非表示なので参照ポップアップからのみ呼ばれる */
        if (opts.readOnly) {
          return <DeliverableSetEditor value={list} onChange={/* istanbul ignore next -- readOnly では DeliverableSetEditor が先に return するため呼ばれない */ () => {}} readOnly />;
        }
        /* istanbul ignore next -- 同上。編集モードの表示経路は存在しない */
        if (list.length === 0) return '—';
        /* istanbul ignore next -- 同上 */
        return `${list[0].product || ''}${list.length > 1 ? ` 他${list.length - 1}件` : ''}`;
      },
    }),
    [deliverableOptionsByKey],
  );

  const columns = useMemo(
    () => {
      const mapped = BASE_COLUMNS.map((col) => {
        if (col.key === 'successor_task') {
          return {
            ...col,
            width: '220px',
            getOptions: ({ row }) => {
              const key = row.oem_region || '';
              return successorTaskOptionsByOemRegion[key] || [''];
            },
          };
        }

        if (col.key === 'related_risk') {
          return {
            ...col,
            width: '220px',
            getOptions: ({ row }) => relatedRiskOptionsByOemRegion[row?.oem_region || ''] || [''],
          };
        }

        if (col.key === 'related_problem') {
          return {
            ...col,
            width: '220px',
            getOptions: ({ row }) => relatedProblemOptionsByOemRegion[row?.oem_region || ''] || [''],
          };
        }

        if (col.key === 'phase_name') {
          return {
            ...col,
            width: '148px',
            options: phaseOptions,
          };
        }

        if (col.key === 'assignees_display') {
          return {
            ...col,
            width: '148px',
            getOptions: () => employeeOptions,
          };
        }

        if (col.key === 'oem_region') {
          return {
            ...col,
            width: (/* istanbul ignore next -- OEM×対地は BASE_COLUMNS で width:'180px' を指定済み */ col.width || '180px'),
            options: oemRegionOptions,
            onChange: ({ row, nextRow, value }) => {
              /* istanbul ignore else -- 同じ値の選び直しでは onChange が発火しない */
              if (value !== row.oem_region) {
                // 成果物は自分の OEM×対地 を持つため、変更後も紐付けたままだと
                // 成果物管理では前の OEM×対地 の行として並び、変更後の
                // OEM×対地 で絞ると見つけられなくなる。
                return { ...nextRow, deliverables: [] };
              }
              /* istanbul ignore next -- 同じ値の選び直しでは onChange が発火しない */
              return nextRow;
            },
          };
        }

        if (col.key === 'large_category_name') {
          return {
            ...col,
            width: (col.width || '148px'),
            options: (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions[col.key] || ['']),
            onChange: ({ row, nextRow, value }) => {
              if (value !== row.large_category_name) {
                // 座標が変わると成果物の紐づき先も変わるので、いったん外す。
                // 新しい座標で設定し直せばそこに登録され、何も設定しなければ解除される。
                return {
                  ...nextRow,
                  middle_category_name: '',
                  small_category: '',
                  deliverables: [],
                };
              }
              return nextRow;
            },
          };
        }

        if (col.key === 'middle_category_name') {
          return {
            ...col,
            width: (col.width || '148px'),
            getOptions: ({ row }) => {
              const large = row.large_category_name;
              if (large && middleCategoryOptionsByLarge[large]) {
                return middleCategoryOptionsByLarge[large];
              }
              return (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions[col.key] || ['']);
            },
            onChange: ({ row, nextRow, value }) => {
              if (value !== row.middle_category_name) {
                // 大項目と同じ理由で成果物も外す
                return { ...nextRow, small_category: '', deliverables: [] };
              }
              return nextRow;
            },
          };
        }

        if (col.key === 'small_category') {
          return {
            ...col,
            width: (/* istanbul ignore next -- 小項目は BASE_COLUMNS で width:'300px' を指定済み */ col.width || '300px'),
            getOptions: ({ row }) => {
              const middle = row.middle_category_name;
              if (middle && smallCategoryOptionsByMiddle[middle]) {
                return smallCategoryOptionsByMiddle[middle];
              }
              return (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions[col.key] || ['']);
            },
            onChange: ({ row, nextRow, value }) => {
              /* istanbul ignore else -- 同じ値の選び直しでは onChange が発火しない */
              if (value !== row.small_category) {
                // 成果物は小項目ごとに紐づくので、変えたらいったん外す
                return { ...nextRow, deliverables: [] };
              }
              /* istanbul ignore next -- 同じ値の選び直しでは onChange が発火しない */
              return nextRow;
            },
          };
        }

        return {
          ...col,
          width: (col.width || '148px'),
          options: col.type === 'select' ? (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions[col.key] || ['']) : col.options,
        };
      });
      // 編集用の成果物セット列を「マイルストーン」の直後に挿入する
      const milestoneIndex = mapped.findIndex((c) => c.key === 'milestone');
      /* istanbul ignore next -- マイルストーン列は BASE_COLUMNS に必ず存在する */
      const insertAt = milestoneIndex >= 0 ? milestoneIndex + 1 : mapped.length;
      mapped.splice(insertAt, 0, deliverablesColumn);
      return mapped;
    },
    [deliverablesColumn, deliverableOptionsByKey, employeeOptions, middleCategoryOptionsByLarge, oemRegionOptions, phaseOptions, relatedProblemOptionsByOemRegion, relatedRiskOptionsByOemRegion, selectOptions, smallCategoryOptionsByMiddle, successorTaskOptionsByOemRegion],
  );

  const deadlineCheckOptions = useMemo(() => {
    const values = new Set();
    normalizedRows.forEach((row) => { if (row.deadline_check) values.add(row.deadline_check); });
    return ['', ...Array.from(values)];
  }, [normalizedRows]);

  const inconsistencyCheckOptions = useMemo(() => {
    const values = new Set();
    normalizedRows.forEach((row) => { if (row.inconsistency_check) values.add(row.inconsistency_check); });
    return ['', ...Array.from(values)];
  }, [normalizedRows]);

  const middleCategoryFilterOptions = useMemo(() => {
    if (!largeCategoryFilter) {
      return (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions.middle_category_name || ['']);
    }
    return (/* istanbul ignore next -- 大項目の選択肢は行から作るため必ず中項目が紐づく */ middleCategoryOptionsByLarge[largeCategoryFilter] || ['']);
  }, [largeCategoryFilter, middleCategoryOptionsByLarge, selectOptions.middle_category_name]);

  const filteredRows = useMemo(() => {
    const keyword = keywordFilter.trim().toLowerCase();

    return normalizedRows.filter((row) => {
      if (deadlineCheckFilter && row.deadline_check !== deadlineCheckFilter) return false;
      if (inconsistencyCheckFilter && row.inconsistency_check !== inconsistencyCheckFilter) return false;
      if (oemRegionFilter && row.oem_region !== oemRegionFilter) return false;
      if (categoryFilter && row.category_name !== categoryFilter) return false;
      if (largeCategoryFilter && row.large_category_name !== largeCategoryFilter) return false;
      if (middleCategoryFilter && row.middle_category_name !== middleCategoryFilter) return false;
      if (actorFilter && row.actor_name !== actorFilter) return false;
      if (statusFilter && row.status_name !== statusFilter) return false;
      if (assigneeFilter && !(row.assignees_display || '').includes(assigneeFilter)) return false;
      if (dateFromFilter) {
        const fromValue = row[dateFromFieldFilter];
        if (!fromValue || fromValue < dateFromFilter) return false;
      }
      if (dateToFilter) {
        const toValue = row[dateToFieldFilter];
        if (!toValue || toValue > dateToFilter) return false;
      }

      if (keyword) {
        const searchable = [
          row.small_category,
          row.memo,
          // 成果物は複数紐付くとカンマ結合された1本の文字列で返る
          row.deliverable_name,
          row.related_risk,
          row.related_problem,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!searchable.includes(keyword)) return false;
      }

      return true;
    });
  }, [
    actorFilter,
    assigneeFilter,
    categoryFilter,
    dateFromFieldFilter,
    dateFromFilter,
    dateToFieldFilter,
    dateToFilter,
    deadlineCheckFilter,
    inconsistencyCheckFilter,
    keywordFilter,
    largeCategoryFilter,
    middleCategoryFilter,
    normalizedRows,
    oemRegionFilter,
    statusFilter,
  ]);

  const clearFilters = () => {
    setDeadlineCheckFilter('');
    setInconsistencyCheckFilter('');
    setOemRegionFilter('');
    setCategoryFilter('');
    setLargeCategoryFilter('');
    setMiddleCategoryFilter('');
    setActorFilter('');
    setStatusFilter('');
    setAssigneeFilter('');
    setDateFromFieldFilter('start_date_plan');
    setDateFromFilter('');
    setDateToFieldFilter('end_date_plan');
    setDateToFilter('');
    setKeywordFilter('');
  };

  const handleLargeCategoryFilterChange = (value) => {
    setLargeCategoryFilter(value);
    /* istanbul ignore next -- 選択肢は行から作るため対応する候補が必ず存在する */
    const nextOptions = value ? (middleCategoryOptionsByLarge[value] || ['']) : (selectOptions.middle_category_name || ['']);
    if (!nextOptions.includes(middleCategoryFilter)) {
      setMiddleCategoryFilter('');
    }
  };

  const progressManagementFilterValues = {
    deadline_check: deadlineCheckFilter,
    inconsistency_check: inconsistencyCheckFilter,
    oem_region: oemRegionFilter,
    category_name: categoryFilter,
    large_category_name: largeCategoryFilter,
    middle_category_name: middleCategoryFilter,
    actor_name: actorFilter,
    status_name: statusFilter,
    assignee: assigneeFilter,
    date_from_field: dateFromFieldFilter,
    date_from: dateFromFilter,
    date_to_field: dateToFieldFilter,
    date_to: dateToFilter,
    keyword: keywordFilter,
  };

  const progressManagementFilterFields = useMemo(
    () => [
      {
        key: 'deadline_check',
        label: '期限チェック',
        type: 'select',
        options: deadlineCheckOptions.map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'inconsistency_check',
        label: '不整合チェック',
        type: 'select',
        options: inconsistencyCheckOptions.map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'oem_region',
        label: 'OEM×対地',
        type: 'select',
        options: oemRegionOptions.map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'category_name',
        label: 'カテゴリ',
        type: 'select',
        options: (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions.category_name || ['']).map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'large_category_name',
        label: '大項目',
        type: 'select',
        options: (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions.large_category_name || ['']).map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'middle_category_name',
        label: '中項目',
        type: 'select',
        options: middleCategoryFilterOptions.map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'actor_name',
        label: '領域',
        type: 'select',
        options: (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions.actor_name || ['']).map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'status_name',
        label: 'ステータス',
        type: 'select',
        options: (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions.status_name || ['']).map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'assignee',
        label: '担当者',
        type: 'select',
        options: employeeOptions.map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'date_from_field',
        label: 'From対象',
        type: 'select',
        options: [
          { value: 'start_date_plan', label: '開始日（予定）' },
          { value: 'end_date_plan', label: '終了日（予定）' },
          { value: 'start_date_achievement', label: '開始日（実績）' },
          { value: 'end_date_achievement', label: '終了日（実績）' },
        ],
      },
      { key: 'date_from', label: '期間From', type: 'date' },
      {
        key: 'date_to_field',
        label: 'To対象',
        type: 'select',
        options: [
          { value: 'start_date_plan', label: '開始日（予定）' },
          { value: 'end_date_plan', label: '終了日（予定）' },
          { value: 'start_date_achievement', label: '開始日（実績）' },
          { value: 'end_date_achievement', label: '終了日（実績）' },
        ],
      },
      { key: 'date_to', label: '期間To', type: 'date' },
      {
        key: 'keyword',
        label: 'キーワード',
        type: 'text',
        className: 'pm-filter-item--keyword',
        placeholder: '小項目 / 備考 / 成果物 / 関連リスク / 関連課題',
      },
    ],
    [deadlineCheckOptions, inconsistencyCheckOptions, employeeOptions, middleCategoryFilterOptions, oemRegionOptions, selectOptions.actor_name, selectOptions.category_name, selectOptions.large_category_name, selectOptions.status_name],
  );

  const handleProgressManagementFilterChange = (key, value) => {
    switch (key) {
      case 'deadline_check':
        setDeadlineCheckFilter(value);
        break;
      case 'inconsistency_check':
        setInconsistencyCheckFilter(value);
        break;
      case 'oem_region':
        setOemRegionFilter(value);
        break;
      case 'category_name':
        setCategoryFilter(value);
        break;
      case 'large_category_name':
        handleLargeCategoryFilterChange(value);
        break;
      case 'middle_category_name':
        setMiddleCategoryFilter(value);
        break;
      case 'actor_name':
        setActorFilter(value);
        break;
      case 'status_name':
        setStatusFilter(value);
        break;
      case 'assignee':
        setAssigneeFilter(value);
        break;
      case 'date_from_field':
        setDateFromFieldFilter(value);
        break;
      case 'date_from':
        setDateFromFilter(value);
        break;
      case 'date_to_field':
        setDateToFieldFilter(value);
        break;
      case 'date_to':
        setDateToFilter(value);
        break;
      case 'keyword':
        setKeywordFilter(value);
        break;
      /* istanbul ignore next -- filterFields で定義済みキーのみ渡るため到達しない防御 default 分岐 */
      default:
        break;
    }
  };

  const handleExportConfirm = async (scope) => {
    const data = scope === 'filtered' ? filteredRows : normalizedRows;
    setExporting(true);
    try {
      await exportToExcel(data, BASE_COLUMNS, '進捗スケジュール');
    } finally {
      setExporting(false);
      setExportPopupOpen(false);
    }
  };

  const buildRefs = () => {
    // 対地OEM + 小項目名 → ProgressSchedule ID のルックアップ
    const scheduleByOemSmall = new Map();
    normalizedRows.forEach((r) => {
      if (r.id && r.oem_region && r.small_category) {
        const key = `${r.oem_region}::${r.small_category}`;
        if (!scheduleByOemSmall.has(key)) {
          scheduleByOemSmall.set(key, r.id);
        }
      }
    });

    return {
      oemByName,
      regionByName,
      oemRegionLookup,
      categoryByName,
      actorByName,
      statusByName,
      phaseByName,
      middleByPair,
      middleByName,
      smallByPair,
      smallByName,
      employeeByName,
      scheduleByOemSmall,
      riskByTitle,
      problemByTitle,
    };
  };

  const buildDefaults = () => ({
    oemId: oemByName.get(defaultCreateValues.oem_name),
    regionId: regionByName.get(defaultCreateValues.region_name),
  });

  const handlePopupDelete = async (row) => {
    /* istanbul ignore if -- 削除ボタンは既存レコード（id あり）にしか出ない */
    if (!row.id) throw new Error('新規レコードは削除できません');
    await deleteProgressSchedule(row.id, row.updated_at_iso ?? null);
    const scheduleData = await getProgressSchedules();
    setRows(scheduleData);
  };

  const handlePopupSave = async (row) => {
    if (row.start_date_plan && row.end_date_plan && row.start_date_plan > row.end_date_plan) {
      throw new Error('開始日（予定）は終了日（予定）より前にしてください');
    }
    if (row.start_date_achievement && row.end_date_achievement && row.start_date_achievement > row.end_date_achievement) {
      throw new Error('開始日（実績）は終了日（実績）より前にしてください');
    }
    // 成果物セット: 成果物名⇄URLの相互必須（成果物1件ごと）
    const deliverableError = validateDeliverables(row.deliverables);
    if (deliverableError) {
      throw new Error(deliverableError);
    }
    const payload = toPayload(row, buildRefs(), buildDefaults());
    if (!payload) throw new Error('必須項目が未入力です');
    let savedId;
    try {
      if (row.id) {
        await updateProgressSchedule(row.id, payload);
        savedId = row.id;
      } else {
        const created = await createProgressSchedule(payload);
        savedId = created?.id;
      }
    } catch (e) {
      if (e.status === 409) {
        throw new Error(buildConflictMessage(e.message));
      }
      throw e;
    }
    const scheduleData = await getProgressSchedules();
    setRows(scheduleData);
    if (savedId != null) {
      setHighlightedRowId(savedId);
      setTimeout(() => setHighlightedRowId(null), 5000);
    }
  };


  return (
    <>
    <DashboardInputLayout
      inputTabIndex={0}
      toolbar={
        !loading && !error ? (
          <DashboardInputToolbar
            countText={`${filteredRows.length} / ${normalizedRows.length} 件を表示`}
            exporting={exporting}
            onAdd={() => setAddSignal((prev) => prev + 1)}
            onExport={() => setExportPopupOpen(true)}
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            showAdd
            showExport
            showFilters={showFilters}
            showFilterToggle
          />
        ) : null
      }
      filters={
        !loading && !error && showFilters ? (
          <DashboardFilterPanel
            fields={progressManagementFilterFields}
            onClear={clearFilters}
            onFieldChange={handleProgressManagementFilterChange}
            values={progressManagementFilterValues}
          />
        ) : null
      }
    >
      {loading && <div>読み込み中...</div>}
      {!loading && error && <div>{error}</div>}
      {!loading && !error && (
      <EditableRecordTable
        addButtonLabel="+ レコード追加"
        columns={columns}
        createEmptyRow={() => createEmptyRow(defaultCreateValues)}
        enableRowPopup
        externalAddSignal={addSignal}
        highlightedRowId={highlightedRowId}
        initialRows={filteredRows}
        onPopupDelete={handlePopupDelete}
        onPopupSave={handlePopupSave}
        readOnly={!canEdit('ダッシュボード')}
        rowPopupTitle="進捗管理レコード詳細"
        showAddButton={false}
      />
      )}
    </DashboardInputLayout>
    <ExportConfirmPopup
      open={exportPopupOpen}
      onClose={() => setExportPopupOpen(false)}
      onExport={handleExportConfirm}
      filteredCount={filteredRows.length}
      totalCount={normalizedRows.length}
      loading={exporting}
      sheetName="進捗スケジュール"
      filterFields={progressManagementFilterFields}
      filterValues={progressManagementFilterValues}
    />
    </>
  );
}
